// AF6 — Assistant view: per-turn metrics for the workspace-assistant service, read from
// GET {assistant}/api/v1/metrics/summary (reverse-proxied via /assistant-api/): a low-frequency
// poll, loading/error states, a sortable durations table, and outcome / escalation / fallback
// breakdown cards. Diagnosing "the chat feels slow" should be a glance here, not a live re-run
// with logs.
//
// BF28 — this whole VIEW is admin-only now, but two of its blocks are member-facing by design
// ("My default engine" + the read-only per-engine stats). They are exported as
// <MemberEngineSection/> and rendered on Profile so a member keeps them.

import { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { Loading, ErrorState, Badge } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { EnginesSection } from './Engines';
import type {
  AssistantMetricsSummary,
  AssistantDurStat,
  AssistantModelRole,
  AssistantMemberEngines,
  AssistantEngineStatRow,
} from '../api/schemas';

const WINDOW_S = 86_400; // 24h — turns are far lower-frequency than gateway tool calls.
const REFRESH_MS = 10_000;

// Turns-per-minute over the summary window (exported for the unit test).
export function turnsPerMin(total: number, windowSeconds: number): number {
  if (windowSeconds <= 0) return 0;
  return (total / windowSeconds) * 60;
}

export function generatedAgo(ms: number): string {
  if (!ms) return 'just now';
  const secs = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (secs < 60) return `${secs}s ago`;
  return `${Math.round(secs / 60)}m ago`;
}

export function Assistant() {
  const res = useResource(() => api.assistantMetricsSummary(WINDOW_S), []);
  // No bus event for assistant metrics — low-frequency poll to keep the figures fresh.
  useEffect(() => {
    const t = setInterval(() => res.reload(true), REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (res.loading) return <Loading what="Loading assistant metrics…" />;
  if (res.error || !res.data)
    return (
      <div className="view">
        <div className="view-head"><h1>Assistant</h1></div>
        <ErrorState message={res.error ?? 'no data'} onRetry={res.reload} />
      </div>
    );

  const summary = res.data;
  return (
    <div className="view">
      <div className="view-head">
        <h1>Assistant</h1>
        <div className="view-head-actions muted small">
          window {Math.round(summary.window_seconds / 3600)}h · {summary.turns.total} turns
          {' '}({turnsPerMin(summary.turns.total, summary.window_seconds).toFixed(2)}/min)
          {' '}· updated {generatedAgo(summary.generated_at_ms)}
        </div>
      </div>

      <MyDefaultEnginePicker />

      <MemberEnginesStats summary={summary} />

      <DurationsTable summary={summary} />

      <div className="perf-grid">
        <OutcomesPanel summary={summary} />
        <EscalationsPanel summary={summary} />
        <FallbacksPanel summary={summary} />
        <ReviewsPanel summary={summary} />
        <ShapePanel summary={summary} />
        <TurnModelsPanel summary={summary} />
        <SubchatsPanel summary={summary} />
      </div>

      {/* UX17 — the engines live HERE now, not in a nav row of their own: an engine
          only means something in the assistant's terms (which default a new
          conversation binds to, which engine served which turns). /engines redirects. */}
      <CollapsibleSection title="Engines" storageKey="assistant.collapsed:engines">
        <EnginesSection />
      </CollapsibleSection>

      <AssistantInstanceSection />

      <ModelRolesSection />
    </div>
  );
}

// --- BF15 + BF18: the assistant's instance framing (admin) -------------------
//
// Two things an admin owns for EVERY conversation on this instance:
//   * BF15 — the DEFAULT REPLY LANGUAGE. The assistant is told to answer in it unless the user
//     switched languages themselves. Ships as Russian.
//   * BF18 — GLOBAL MEMORY: system-wide facts/instructions injected read-only into every
//     conversation's framing, ABOVE the per-user and per-project memory layers.
//
// Both live in one gateway settings blob; the whole section self-hides when the endpoint is not
// available (an older gateway), so the view degrades instead of erroring.
const LANGUAGES: { code: string; label: string }[] = [
  { code: 'ru', label: 'Русский (ru)' },
  { code: 'en', label: 'English (en)' },
  { code: 'de', label: 'Deutsch (de)' },
  { code: 'fr', label: 'Français (fr)' },
  { code: 'es', label: 'Español (es)' },
  { code: 'zh', label: '中文 (zh)' },
];

/**
 * CL17 — choosing the model the assistant SPEAKS with.
 *
 * The honest scope of this control is worth stating on the card, because "the assistant's model" is
 * two different things depending on which lane you mean, and a picker that pretended otherwise
 * would be worse than no picker at all:
 *
 * * The **call** lane talks to `text-llm` directly, and the model is an argument of every request.
 *   Choosing one here takes effect on the next call, end to end, and is visible in `text-llm`'s own
 *   logs. That is what this control does.
 * * The **chat** lane goes through an ENGINE (AEP). The engine owns its model — it is a property of
 *   the running instance, chosen when that instance was built, and the protocol has no per-turn
 *   model field by design (the chat module deliberately knows nothing about models). Which engine a
 *   conversation runs on is chosen above, in "Engines"; which model that engine uses is a property
 *   of its deployment. Saying so out loud is the point: a select box here that silently did nothing
 *   to the chat lane is exactly the kind of control that costs somebody an afternoon.
 *
 * The options come from the service rather than from a constant, so a model that lands on a node
 * can be selected the same day. When the catalogue cannot be reached the select degrades to a text
 * field: an admin who knows the id must not be blocked by a dashboard that cannot list it.
 */
export function CallModelRow({ selected, busy, onPick }: {
  selected: string;
  busy: boolean;
  onPick: (model: string) => void;
}) {
  const models = useResource(() => api.getAssistantModels(), []);
  const local = models.data?.models ?? [];
  // CALL-FAST — the hosted options, labelled as what they are. They are appended rather than
  // merged: the two kinds of model behave differently in ways an admin finds out about at the worst
  // possible moment otherwise — a local one is warmed and pinned for the length of the call, an
  // external one runs on a third-party account and can hit that account's limit mid-conversation.
  // A picker that hides that distinction is offering a choice without its consequences.
  const external = models.data?.external ?? [];
  const available = [...local, ...external];
  // A model set by hand (or one since retired from the catalogue) must still be shown as selected,
  // or the picker would silently misreport what the calls are actually using.
  const options = selected && !available.includes(selected) ? [selected, ...available] : available;
  const label = (m: string) => (external.includes(m) ? `${m} — external, experimental` : m);
  return (
    <div className="detail-row" data-testid="assistant-call-model">
      <span className="detail-label">
        Call model
        <div className="muted small">
          the model the assistant SPEAKS with; applies to the next call. Chat replies run on the
          engine chosen above, and the engine owns its own model.
        </div>
      </span>
      <span className="detail-value">
        {options.length > 0 ? (
          <select
            aria-label="Assistant call model"
            value={selected}
            disabled={busy || models.loading}
            onChange={(e) => onPick(e.target.value)}
          >
            <option value="">— deployment default —</option>
            {options.map((m) => (
              <option key={m} value={m}>{label(m)}</option>
            ))}
          </select>
        ) : (
          <input
            aria-label="Assistant call model"
            type="text"
            placeholder="deployment default"
            defaultValue={selected}
            disabled={busy}
            onBlur={(e) => {
              if (e.target.value.trim() !== selected) onPick(e.target.value.trim());
            }}
          />
        )}
      </span>
    </div>
  );
}

export function AssistantInstanceSection() {
  const res = useResource(() => api.getAssistantConfig(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setErr(null);
      try {
        await fn();
        await res.reload(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'update failed');
      } finally {
        setBusy(false);
      }
    },
    [res],
  );

  // An older gateway has no /assistant/config — hide rather than show a broken card.
  if (res.loading || res.error || !res.data) return null;
  const cfg = res.data;
  // A language the admin set by hand (or a future gateway default) must still be selectable.
  const options = LANGUAGES.some((l) => l.code === cfg.language)
    ? LANGUAGES
    : [{ code: cfg.language, label: cfg.language }, ...LANGUAGES];

  return (
    <section className="card" data-testid="assistant-instance">
      <header className="card-head">
        <h2>Assistant language &amp; global memory</h2>
        <span className="muted small">applies to every conversation on this instance</span>
      </header>
      <div className="card-body">
        {err && <div className="muted small" role="alert">Update failed: {err}</div>}
        <div className="detail-row">
          <span className="detail-label">
            Default reply language
            <div className="muted small">used unless the user switches languages themselves</div>
          </span>
          <span className="detail-value">
            <select
              aria-label="Default assistant language"
              value={cfg.language}
              disabled={busy}
              onChange={(e) => void run(() => api.updateAssistantConfig({ language: e.target.value }))}
            >
              {options.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </span>
        </div>

        <CallModelRow
          selected={cfg.call_model}
          busy={busy}
          onPick={(model) => void run(() => api.updateAssistantConfig({ call_model: model }))}
        />

        <div className="detail-row">
          <span className="detail-label">
            Global memory
            <div className="muted small">
              injected read-only into every conversation, above user and project memory
            </div>
          </span>
          <span className="detail-value">
            <ul className="plain-list" data-testid="global-memory-list">
              {cfg.global_memory.length === 0 && <li className="muted small">No global facts yet.</li>}
              {cfg.global_memory.map((n) => (
                <li key={n.id} data-testid="global-memory-item">
                  <span>{n.text}</span>{' '}
                  <button
                    className="btn small"
                    aria-label={`Forget ${n.text}`}
                    disabled={busy}
                    onClick={() => void run(() => api.deleteGlobalMemory(n.id))}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <input
              aria-label="Add a global fact"
              placeholder="e.g. Always answer using the metric system"
              value={draft}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
            />{' '}
            <button
              className="btn small"
              disabled={busy || draft.trim().length === 0}
              onClick={() =>
                void run(async () => {
                  await api.addGlobalMemory(draft.trim());
                  setDraft('');
                })
              }
            >
              Add
            </button>
          </span>
        </div>
      </div>
    </section>
  );
}

// BF28 — the member-facing half of this view, packaged for Profile (the only tab a non-admin sees).
// Both children already self-hide when their endpoint is unavailable or there is nothing to choose,
// and the metrics summary is fetched here with a `.catch(() => null)` so a member who cannot read it
// still gets the picker (the stats table is simply omitted).
export function MemberEngineSection() {
  const summary = useResource(() => api.assistantMetricsSummary(WINDOW_S).catch(() => null), []);
  return (
    <>
      <MyDefaultEnginePicker />
      {summary.data ? <MemberEnginesStats summary={summary.data} /> : null}
    </>
  );
}

// --- Member engine picker (#2) ----------------------------------------------

// The caller's PER-USER default engine. Shown only when the assistant reports >1 selectable engine
// (a single-engine deploy has nothing to pick). PUT on change, then re-read. `engine:null` reverts to
// the org/global default. This is the member-facing counterpart to the admin runtime-default control
// in Engines.tsx — it sets only the caller's own binding, not the org default.
function MyDefaultEnginePicker() {
  const res = useResource(() => api.getMyDefaultEngine(), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const choose = useCallback(
    async (engine: string | null) => {
      setBusy(true);
      setErr(null);
      try {
        await api.setMyDefaultEngine(engine);
        await res.reload(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'update failed');
      } finally {
        setBusy(false);
      }
    },
    [res],
  );

  // Hide the whole section when the member endpoint is unavailable (older assistant / not a member)
  // or the deploy has a single engine — there is nothing to choose.
  if (res.loading || res.error || !res.data || !res.data.selectable) return null;

  const d = res.data;
  const isMine = d.source === 'user';
  return (
    <section className="card">
      <header className="card-head">
        <h2>My default engine</h2>
        <span className="muted small">new conversations of yours bind to this engine</span>
      </header>
      <div className="card-body">
        {err && <div className="muted small" role="alert">Update failed: {err}</div>}
        <div className="detail-row">
          <span className="detail-label">
            <select
              aria-label="My default engine"
              value={d.engine}
              disabled={busy}
              onChange={(e) => choose(e.target.value)}
            >
              {d.engines.map((e) => (
                <option key={e.name} value={e.name}>
                  {e.name === d.global_default ? `${e.name} (org default)` : e.name}
                </option>
              ))}
            </select>
          </span>
          <span className="detail-value">
            {isMine ? (
              <>
                <Badge tone="info">yours</Badge>{' '}
                <button className="btn small" disabled={busy} onClick={() => choose(null)}>
                  Reset to org default
                </button>
              </>
            ) : (
              <Badge tone="muted">org default</Badge>
            )}
          </span>
        </div>
      </div>
    </section>
  );
}

// --- Member per-engine stats (#1) -------------------------------------------

interface MemberEngineRow {
  name: string;
  is_default: boolean;
  stats: AssistantEngineStatRow | null;
}

// Merge the member engine list (WHICH engines exist + default flag) with the per-engine metrics
// rollup (may be absent for an engine with no turns in the window). The read-only member counterpart
// to mergeEngineRows() in Engines.tsx — no admin health/capabilities. Exported for the unit test.
export function mergeMemberEngineRows(
  engines: AssistantMemberEngines,
  summary: AssistantMetricsSummary,
): MemberEngineRow[] {
  const statsByEngine = new Map(summary.engines.map((e) => [e.engine, e]));
  return engines.engines.map((e) => ({
    name: e.name,
    is_default: e.is_default,
    stats: statsByEngine.get(e.name) ?? null,
  }));
}

function engMs(v: number | undefined): string {
  return v && v > 0 ? `${Math.round(v)}ms` : '—';
}

// Compact, read-only per-engine stats for members: turns, TTFT/turn percentiles and outcomes from
// GET /api/v1/engines + summary.engines. No health/capabilities (admin-only, in Engines.tsx).
function MemberEnginesStats({ summary }: { summary: AssistantMetricsSummary }) {
  const res = useResource(() => api.getMemberEngines(), []);
  // Hide when the member endpoint is unavailable (older assistant / not a member).
  if (res.loading || res.error || !res.data) return null;
  const rows = mergeMemberEngineRows(res.data, summary);
  if (rows.length === 0) return null;
  return (
    <section className="card">
      <header className="card-head">
        <h2>Engines</h2>
        <span className="muted small">
          per-engine turns · window {Math.round(summary.window_seconds / 3600)}h
        </span>
      </header>
      <div className="card-body">
        <table className="data-table">
          <thead>
            <tr>
              <th>Engine</th>
              <th style={{ textAlign: 'right' }}>Turns</th>
              <th style={{ textAlign: 'right' }}>TTFT p50</th>
              <th style={{ textAlign: 'right' }}>TTFT p95</th>
              <th style={{ textAlign: 'right' }}>Turn p50</th>
              <th style={{ textAlign: 'right' }}>Turn p95</th>
              <th style={{ textAlign: 'right' }}>ok</th>
              <th style={{ textAlign: 'right' }}>escalated</th>
              <th style={{ textAlign: 'right' }}>degraded</th>
              <th style={{ textAlign: 'right' }}>cancelled</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const o = r.stats?.outcomes ?? {};
              const dm = r.stats?.durations_ms;
              return (
                <tr key={r.name}>
                  <td>
                    {r.name}
                    {r.is_default && <> <Badge tone="ok">default</Badge></>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{r.stats?.turns ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>{engMs(dm?.ttft.p50_ms)}</td>
                  <td style={{ textAlign: 'right' }}>{engMs(dm?.ttft.p95_ms)}</td>
                  <td style={{ textAlign: 'right' }}>{engMs(dm?.turn.p50_ms)}</td>
                  <td style={{ textAlign: 'right' }}>{engMs(dm?.turn.p95_ms)}</td>
                  <td style={{ textAlign: 'right' }}>{o.local ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>{o.escalated ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>{o.degraded ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>{o.cancelled ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// --- MC3: Models section (role → model) -------------------------------------

/** The `<select>` options for a role: the LIVE model list, plus the role's current value if it is
 *  NOT in the live list (a stale override still shows honestly), each marked when it is the env
 *  default. Exported for the unit test. */
export function modelOptions(
  models: string[],
  role: AssistantModelRole,
): { value: string; label: string }[] {
  const set = new Set(models);
  const values = [...models];
  if (role.current && !set.has(role.current)) values.unshift(role.current);
  return values.map((m) => ({ value: m, label: m === role.default ? `${m} (default)` : m }));
}

/** The escalation role is OpenClaw (a provider, NOT a vLLM model) — its model select is disabled. */
export function isEscalation(role: AssistantModelRole): boolean {
  return role.provider !== 'vllm';
}

interface RoleDraft {
  model: string;
  allowlist: string[];
}

function ModelRolesSection() {
  const res = useResource(async () => {
    const [rolesRes, modelsRes] = await Promise.all([api.getModelRoles(), api.getModels()]);
    return { roles: rolesRes.roles, models: modelsRes.models };
  }, []);
  const [draft, setDraft] = useState<Record<string, RoleDraft>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Seed the editable draft from the loaded config (and re-seed after each save/reset reload).
  useEffect(() => {
    if (!res.data) return;
    const d: Record<string, RoleDraft> = {};
    for (const r of res.data.roles) d[r.role] = { model: r.current ?? '', allowlist: r.allowlist };
    setDraft(d);
  }, [res.data]);

  const save = useCallback(
    async (role: AssistantModelRole) => {
      setBusy(role.role);
      setErr(null);
      try {
        const d = draft[role.role];
        await api.putModelRoles([
          {
            role: role.role,
            model: d?.model || null,
            allowlist: role.supports_allowlist ? d?.allowlist ?? [] : undefined,
          },
        ]);
        await res.reload(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'save failed');
      } finally {
        setBusy(null);
      }
    },
    [draft, res],
  );

  const reset = useCallback(
    async (role: AssistantModelRole) => {
      setBusy(role.role);
      setErr(null);
      try {
        await api.putModelRoles([{ role: role.role, reset: true }]);
        await res.reload(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'reset failed');
      } finally {
        setBusy(null);
      }
    },
    [res],
  );

  if (res.loading)
    return (
      <section className="card">
        <header className="card-head"><h2>Models</h2></header>
        <div className="card-body"><Loading what="Loading model roles…" /></div>
      </section>
    );
  if (res.error || !res.data)
    return (
      <section className="card">
        <header className="card-head"><h2>Models</h2></header>
        <div className="card-body"><ErrorState message={res.error ?? 'no data'} onRetry={res.reload} /></div>
      </section>
    );

  const models = res.data.models;
  return (
    <section className="card">
      <header className="card-head">
        <h2>Models</h2>
        <span className="muted small">role → model · dropdown is live from text-llm</span>
      </header>
      <div className="card-body">
        {err && <div className="muted small" role="alert">Save failed: {err}</div>}
        <table className="data-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Model</th>
              <th>Source</th>
              <th>Allow-list</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {res.data.roles.map((role) => {
              const d = draft[role.role] ?? { model: role.current ?? '', allowlist: role.allowlist };
              const esc = isEscalation(role);
              const rowBusy = busy === role.role;
              return (
                <tr key={role.role}>
                  <td>
                    <div>{role.role}</div>
                    <div className="muted small">{role.description}</div>
                  </td>
                  <td>
                    {esc ? (
                      <Badge tone="info">OpenClaw</Badge>
                    ) : (
                      <select
                        aria-label={`${role.role} model`}
                        value={d.model}
                        disabled={rowBusy}
                        onChange={(e) =>
                          setDraft({ ...draft, [role.role]: { ...d, model: e.target.value } })
                        }
                      >
                        {modelOptions(models, role).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <Badge tone={role.source === 'override' ? 'warn' : 'muted'}>{role.source}</Badge>
                  </td>
                  <td>
                    {role.supports_allowlist ? (
                      <select
                        multiple
                        aria-label={`${role.role} allow-list`}
                        value={d.allowlist}
                        disabled={rowBusy}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            [role.role]: {
                              ...d,
                              allowlist: Array.from(e.target.selectedOptions, (o) => o.value),
                            },
                          })
                        }
                      >
                        {models.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                  <td>
                    {!esc && (
                      <>
                        <button className="btn small" disabled={rowBusy} onClick={() => save(role)}>
                          Save
                        </button>{' '}
                        <button
                          className="btn small"
                          disabled={rowBusy || role.source === 'default'}
                          onClick={() => reset(role)}
                        >
                          Reset
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface DurRow {
  phase: string;
  stat: AssistantDurStat;
}

function DurationsTable({ summary }: { summary: AssistantMetricsSummary }) {
  const d = summary.durations_ms;
  const rows: DurRow[] = [
    { phase: 'time-to-first-token', stat: d.ttft },
    { phase: 'classify', stat: d.classify },
    { phase: 'think (escalation)', stat: d.think },
    { phase: 'whole turn', stat: d.turn },
  ];
  const cols: Column<DurRow>[] = [
    { key: 'phase', header: 'Phase', cell: (r) => r.phase, sortValue: (r) => r.phase },
    { key: 'count', header: 'Samples', align: 'right', cell: (r) => r.stat.count, sortValue: (r) => r.stat.count },
    { key: 'p50', header: 'p50', align: 'right', cell: (r) => `${Math.round(r.stat.p50_ms)}ms`, sortValue: (r) => r.stat.p50_ms },
    { key: 'p95', header: 'p95', align: 'right', cell: (r) => `${Math.round(r.stat.p95_ms)}ms`, sortValue: (r) => r.stat.p95_ms },
    { key: 'max', header: 'max', align: 'right', cell: (r) => `${Math.round(r.stat.max_ms)}ms`, sortValue: (r) => r.stat.max_ms },
  ];
  return (
    <section className="card">
      <header className="card-head"><h2>Turn latency (p50 / p95)</h2></header>
      <div className="card-body">
        <DataTable
          columns={cols}
          rows={rows}
          rowKey={(r) => r.phase}
          empty="No turns recorded in this window."
          initialSort={{ key: 'p95', dir: 'desc' }}
        />
      </div>
    </section>
  );
}

// Ordered known keys → a stable, readable badge row, plus a total.
function Breakdown({ counts, order, tones }: {
  counts: Record<string, number>;
  order: string[];
  tones?: Record<string, 'ok' | 'warn' | 'bad' | 'info' | 'muted'>;
}) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="muted small">None in this window.</div>;
  return (
    <>
      {order
        .filter((k) => (counts[k] ?? 0) > 0)
        .map((k) => (
          <div key={k} className="detail-row">
            <span className="detail-label">{k.replace(/_/g, ' ')}</span>
            <span className="detail-value">
              <Badge tone={tones?.[k] ?? 'info'}>{counts[k] ?? 0}</Badge>
            </span>
          </div>
        ))}
    </>
  );
}

function OutcomesPanel({ summary }: { summary: AssistantMetricsSummary }) {
  return (
    <section className="card">
      <header className="card-head"><h2>Outcomes</h2></header>
      <div className="card-body">
        <Breakdown
          counts={summary.turns.outcomes}
          order={['local', 'escalated', 'degraded', 'cancelled']}
          tones={{ local: 'ok', escalated: 'info', degraded: 'warn', cancelled: 'muted' }}
        />
      </div>
    </section>
  );
}

function EscalationsPanel({ summary }: { summary: AssistantMetricsSummary }) {
  return (
    <section className="card">
      <header className="card-head"><h2>Escalation triggers</h2></header>
      <div className="card-body">
        <Breakdown
          counts={summary.turns.escalation_reasons}
          order={['model', 'second_action', 'classifier']}
          tones={{ model: 'info', second_action: 'info', classifier: 'info' }}
        />
      </div>
    </section>
  );
}

function FallbacksPanel({ summary }: { summary: AssistantMetricsSummary }) {
  return (
    <section className="card">
      <header className="card-head"><h2>Fallbacks</h2></header>
      <div className="card-body">
        <Breakdown
          counts={summary.turns.fallbacks}
          order={['non_stream', 'history_recovery', 'escalation_unusable']}
          tones={{ non_stream: 'warn', history_recovery: 'warn', escalation_unusable: 'bad' }}
        />
      </div>
    </section>
  );
}

function ReviewsPanel({ summary }: { summary: AssistantMetricsSummary }) {
  return (
    <section className="card">
      <header className="card-head"><h2>Background reviews</h2></header>
      <div className="card-body">
        <div className="detail-row">
          <span className="detail-label">total</span>
          <span className="detail-value">{summary.reviews.total}</span>
        </div>
        <Breakdown
          counts={summary.reviews.verdicts}
          order={['ok', 'correction']}
          tones={{ ok: 'ok', correction: 'warn' }}
        />
        <div className="detail-row">
          <span className="detail-label">corrections applied</span>
          <span className="detail-value">{summary.turns.corrections_applied}</span>
        </div>
      </div>
    </section>
  );
}

// MC10 — subchat done-rate over the terminal transitions (done / failed / cancelled). Exported
// for the unit test. Returns 0 when nothing terminated yet.
export function subchatDoneRate(events: Record<string, number>): number {
  const done = events.done ?? 0;
  const terminal = done + (events.failed ?? 0) + (events.cancelled ?? 0);
  return terminal > 0 ? done / terminal : 0;
}

// MC10 — the per-model turn breakdown (which model each turn ran on + its role).
function TurnModelsPanel({ summary }: { summary: AssistantMetricsSummary }) {
  const rows = summary.turns.by_model;
  return (
    <section className="card">
      <header className="card-head"><h2>Turns by model</h2></header>
      <div className="card-body">
        {rows.length === 0 ? (
          <div className="muted small">No turns recorded in this window.</div>
        ) : (
          rows.map((r) => (
            <div key={`${r.model}:${r.role}`} className="detail-row">
              <span className="detail-label">
                {r.model} <span className="muted small">({r.role})</span>
              </span>
              <span className="detail-value"><Badge tone="info">{r.count}</Badge></span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// MC10 — non-blocking subchat statistics: lifecycle counters, done-rate, duration + concurrency.
function SubchatsPanel({ summary }: { summary: AssistantMetricsSummary }) {
  const sc = summary.subchats;
  const rate = subchatDoneRate(sc.events);
  return (
    <section className="card">
      <header className="card-head">
        <h2>Subchats</h2>
        <span className="muted small">{sc.total_spawned} spawned · done-rate {(rate * 100).toFixed(0)}%</span>
      </header>
      <div className="card-body">
        <Breakdown
          counts={sc.events}
          order={['spawned', 'done', 'failed', 'cancelled']}
          tones={{ spawned: 'info', done: 'ok', failed: 'bad', cancelled: 'muted' }}
        />
        <div className="detail-row">
          <span className="detail-label">active now</span>
          <span className="detail-value">{sc.active}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">max concurrent</span>
          <span className="detail-value">{sc.max_active}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">duration p50</span>
          <span className="detail-value">{Math.round(sc.duration_ms.p50_ms)}ms</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">duration p95</span>
          <span className="detail-value">{Math.round(sc.duration_ms.p95_ms)}ms</span>
        </div>
        {sc.by_model.length > 0 && (
          <>
            <div className="detail-row"><span className="detail-label muted small">by model</span><span /></div>
            {sc.by_model.map((m) => (
              <div key={m.model} className="detail-row">
                <span className="detail-label">{m.model}</span>
                <span className="detail-value muted small">
                  {m.spawned} spawned · {m.done} done · {m.failed} failed · {m.cancelled} cancelled
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function ShapePanel({ summary }: { summary: AssistantMetricsSummary }) {
  const pc = summary.prompt_chars;
  const ti = summary.tool_iterations;
  return (
    <section className="card">
      <header className="card-head"><h2>Prompt & tool loop</h2></header>
      <div className="card-body">
        <div className="detail-row"><span className="detail-label">prompt chars p50</span><span className="detail-value">{Math.round(pc.p50)}</span></div>
        <div className="detail-row"><span className="detail-label">prompt chars p95</span><span className="detail-value">{Math.round(pc.p95)}</span></div>
        <div className="detail-row"><span className="detail-label">prompt chars max</span><span className="detail-value">{Math.round(pc.max)}</span></div>
        <div className="detail-row"><span className="detail-label">tool iters avg</span><span className="detail-value">{ti.avg.toFixed(2)}</span></div>
        <div className="detail-row"><span className="detail-label">tool iters max</span><span className="detail-value">{Math.round(ti.max)}</span></div>
      </div>
    </section>
  );
}
